import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

export const ONE_DIGIT = /^[0-9]$/;
export const ZERO_TO_TWO_DIGITS = /^[0-9]{0,2}$/;
export const VALID_FORMATS = ["hh:mm A", "h:mm A", "hh:m A", "h:m A"];
export const VALID_FORMATS_PRESELECTED = ["hh:mm A", "h:mm A", "HHmm"];

/** Parse preselected values and min/max boundaries in either 12h or 24h form. */
export const ALL_SELECTOR_PRESELECT_FORMATS = ["hh:mm A", "h:mm A", "HHmm", "HH:mm", "H:mm"];

export function parsePreselectedSessionTime(input: string | number): dayjs.Dayjs | null {
	const padded = padTimes(input);
	const t = dayjs(padded, ALL_SELECTOR_PRESELECT_FORMATS, true);
	return t.isValid() ? t : null;
}

// Dayjs can't parse three digit times e.g. 930 correctly, so we need to pad them with a zero
export const padTimes: (time: string | number) => string = (time) => {
	const result = `${time}`;
	if (result.length === 3) {
		return `0${time}`;
	}
	return result;
};

export const oneMinuteFromNow: () => dayjs.Dayjs = () => dayjs().startOf("minute").add(1, "minute");

export const validate: (
	inputHour: string,
	inputMinute: string,
	inputPeriod: string,
	shouldValidateFuture: boolean,
	minTimeObject?: dayjs.Dayjs,
	maxTimeObject?: dayjs.Dayjs
) => {
	result: number | null;
	error: string | null;
} = (inputHour, inputMinute, inputPeriod, shouldValidateFuture, minTimeObject, maxTimeObject) => {
	let timeObject: dayjs.Dayjs;

	// Don't validate if both inputs are empty
	if (inputHour.length === 0 && inputMinute.length === 0) {
		return { result: null, error: "" };
	}

	// Partially validate if any of the inputs is empty
	if (inputHour.length === 0) {
		timeObject = dayjs(`06:${inputMinute} ${inputPeriod}`, VALID_FORMATS, true);
		if (timeObject.isValid()) {
			return { result: null, error: "" };
		}

		return { result: null, error: "Enter valid time" };
	}

	if (inputMinute.length === 0) {
		timeObject = dayjs(`${inputHour}:00 ${inputPeriod}`, VALID_FORMATS, true);
		if (timeObject.isValid()) {
			return { result: null, error: "" };
		}

		return { result: null, error: "Enter valid time" };
	}

	// Ensure the time is in 12-hour format
	timeObject = dayjs(`${inputHour}:${inputMinute} ${inputPeriod}`, VALID_FORMATS, true);
	if (!timeObject.isValid()) {
		return { result: null, error: "Enter valid time" };
	}

	// Optional, default on - ensure the end time is in the future
	if (shouldValidateFuture && timeObject.isBefore(oneMinuteFromNow())) {
		return { result: null, error: "End time cannot be in the past" };
	}

	// Optional - ensure the time one minute later than the min time
	if (minTimeObject && timeObject.isBefore(minTimeObject)) {
		return { result: null, error: "End time can't be earlier than the start time" };
	}

	// Optional - ensure the time one minute earlier than the max time
	if (maxTimeObject && timeObject.isAfter(maxTimeObject)) {
		return { result: null, error: "Start time can't be later than the end time" };
	}

	// Backend expects time in 24-hour format in number
	return { result: +timeObject.format("HHmm"), error: null };
};

const VALID_24H_PAIR_FORMATS = ["HH:mm", "H:mm"];

/** 24-hour display/input: same numeric HHmm result as validate(); no AM/PM. */
export const validate24Hour: (
	inputHour: string,
	inputMinute: string,
	shouldValidateFuture: boolean,
	minTimeObject?: dayjs.Dayjs,
	maxTimeObject?: dayjs.Dayjs
) => { result: number | null; error: string | null } = (inputHour, inputMinute, shouldValidateFuture, minTimeObject, maxTimeObject) => {
	if (inputHour.length === 0 && inputMinute.length === 0) {
		return { result: null, error: "" };
	}

	if (inputHour.length === 0) {
		const timeObject = dayjs(`06:${inputMinute}`, VALID_24H_PAIR_FORMATS, true);
		if (timeObject.isValid() && timeObject.minute() <= 59) {
			return { result: null, error: "" };
		}
		return { result: null, error: "Enter valid time" };
	}

	if (inputMinute.length === 0) {
		const timeObject = dayjs(`${inputHour}:00`, VALID_24H_PAIR_FORMATS, true);
		if (timeObject.isValid() && timeObject.hour() >= 0 && timeObject.hour() <= 23) {
			return { result: null, error: "" };
		}
		return { result: null, error: "Enter valid time" };
	}

	const timeObject = dayjs(`${inputHour}:${inputMinute}`, VALID_24H_PAIR_FORMATS, true);
	if (!timeObject.isValid() || timeObject.hour() > 23 || timeObject.minute() > 59) {
		return { result: null, error: "Enter valid time" };
	}

	if (shouldValidateFuture && timeObject.isBefore(oneMinuteFromNow())) {
		return { result: null, error: "End time cannot be in the past" };
	}

	if (minTimeObject && timeObject.isBefore(minTimeObject)) {
		return { result: null, error: "End time can't be earlier than the start time" };
	}

	if (maxTimeObject && timeObject.isAfter(maxTimeObject)) {
		return { result: null, error: "Start time can't be later than the end time" };
	}

	return { result: +timeObject.format("HHmm"), error: null };
};
